/**
 * Client Portal Dashboard
 *
 * Tableau de bord pour les clients
 * Design System: Contexte DASHBOARD (text-gray-900, bg-white/70)
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban,
  FileCheck,
  MessageCircle,
  TrendingUp,
  ArrowRight,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useClientPortalDashboard, usePendingSituations } from '../hooks/useClientPortal';
import { useClientPortalAuthStore } from '../stores/clientPortalAuth.store';

export function ClientPortalDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useClientPortalAuthStore();
  const { data: stats, isLoading: statsLoading } = useClientPortalDashboard();
  const { data: pendingSituations, isLoading: pendingLoading } = usePendingSituations();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    });
  };

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('common.clientPortal.dashboard.greeting')}{user?.fullName ? `, ${user.fullName.split(' ')[0]}` : ''} !
        </h1>
        <p className="text-gray-500 mt-1">
          {t('common.clientPortal.dashboard.overview')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/40 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-teal-100 rounded-xl">
              <FolderKanban className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('common.clientPortal.dashboard.projects')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.projectsCount || 0}</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {t('common.clientPortal.dashboard.ofWhich')} <span className="font-semibold text-teal-600">{stats?.activeProjectsCount || 0}</span> {t('common.clientPortal.dashboard.activeProjects')}
          </p>
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/40 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <FileCheck className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('common.clientPortal.dashboard.toValidate')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.pendingValidations || 0}</p>
            </div>
          </div>
          {(stats?.pendingValidations || 0) > 0 && (
            <button
              onClick={() => navigate('/portal/projects')}
              className="mt-3 text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 transition-colors"
            >
              {t('common.clientPortal.dashboard.viewSituations')}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/40 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <MessageCircle className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('common.clientPortal.layout.messages')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.unreadMessages || 0}</p>
            </div>
          </div>
          {(stats?.unreadMessages || 0) > 0 && (
            <button
              onClick={() => navigate('/portal/messages')}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors"
            >
              {t('common.clientPortal.dashboard.readMessages')}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/40 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-teal-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('common.clientPortal.dashboard.invoiced')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats?.totalInvoiced || 0)}
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {t('common.clientPortal.dashboard.outOf')} <span className="font-semibold">{formatCurrency(stats?.totalBudget || 0)}</span> {t('common.clientPortal.dashboard.budget')}
          </p>
        </div>
      </div>

      {/* Situations à valider */}
      {(pendingSituations?.length || 0) > 0 && (
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/40 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('common.clientPortal.dashboard.pendingSituations')}
                </h2>
              </div>
              <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                {pendingSituations?.length} {t('common.clientPortal.dashboard.pending')}
              </span>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {pendingSituations?.slice(0, 5).map((situation) => (
              <div
                key={situation.situationId}
                className="p-4 hover:bg-gray-50/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/portal/projects/${situation.projectId}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <FileCheck className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {t('common.clientPortal.dashboard.situationNumber', { number: situation.situationNumber })}
                      </p>
                      <p className="text-sm text-gray-500">{situation.projectName}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(situation.periodAmountTTC)}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(situation.submittedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {(pendingSituations?.length || 0) > 5 && (
            <div className="p-4 bg-gray-50/50 border-t border-gray-200/50">
              <button
                onClick={() => navigate('/portal/projects')}
                className="w-full text-center text-teal-600 hover:text-teal-700 font-medium text-sm transition-colors"
              >
                {t('common.clientPortal.dashboard.viewAllSituations', { count: pendingSituations?.length })}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Budget Progress */}
      {stats && stats.totalBudget > 0 && (
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/40 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('common.clientPortal.dashboard.globalProgress')}</h2>

          <div className="space-y-4">
            {/* Progression facturation */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{t('common.clientPortal.dashboard.invoicing')}</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round((stats.totalInvoiced / stats.totalBudget) * 100)}%
                </span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min((stats.totalInvoiced / stats.totalBudget) * 100, 100)}%`,
                  }}
                />
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>{t('common.clientPortal.dashboard.invoicedAmount', { amount: formatCurrency(stats.totalInvoiced) })}</span>
                <span>{t('common.clientPortal.dashboard.budgetAmount', { amount: formatCurrency(stats.totalBudget) })}</span>
              </div>
            </div>

            {/* Progression paiements */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{t('common.clientPortal.dashboard.payments')}</span>
                <span className="text-sm font-medium text-gray-900">
                  {stats.totalInvoiced > 0
                    ? Math.round((stats.totalPaid / stats.totalInvoiced) * 100)
                    : 0}%
                </span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${
                      stats.totalInvoiced > 0
                        ? Math.min((stats.totalPaid / stats.totalInvoiced) * 100, 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>{t('common.clientPortal.dashboard.paidAmount', { amount: formatCurrency(stats.totalPaid) })}</span>
                <span>{t('common.clientPortal.dashboard.remainingAmount', { amount: formatCurrency(stats.totalInvoiced - stats.totalPaid) })}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {stats?.projectsCount === 0 && (
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-12 border border-white/40 shadow-sm text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <FolderKanban className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('common.clientPortal.dashboard.noProjects')}
          </h3>
          <p className="text-gray-500">
            {t('common.clientPortal.dashboard.projectsWillAppear')}
          </p>
        </div>
      )}
    </div>
  );
}
